import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Sparkles, CheckCircle, Calendar, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function MaintenancePlanGuide() {
  const navigate = useNavigate();

  const dailyHabits = [
    "Morning intention or meditation (5 min)",
    "Hydration (64 oz water)",
    "Movement of some kind",
    "Skincare routine AM/PM",
    "Gratitude reflection",
    "Screen-free wind-down"
  ];

  const weeklyHabits = [
    "Full body stretch or yoga session",
    "Meal planning and prep",
    "Social connection (call or meet a friend)",
    "One celebratory self-care act",
    "Review and adjust goals",
    "Nature time"
  ];

  const monthlyHabits = [
    "Skin assessment and adjust products",
    "Closet check-in",
    "Review finances and goals",
    "Try something new",
    "Deep clean one area",
    "Schedule any needed appointments"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-4">
            Week 12 • Day 1 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Maintenance Plan Creation
          </h1>
          <p className="text-xl text-secondary-600">Keep Your Glow Going</p>
        </div>

        <section className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Trophy className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">You're at the Finish Line!</h3>
              <p className="text-emerald-100 text-lg">
                This final week is about creating a sustainable plan to maintain everything you've built. 
                Which habits will you keep? What will your ongoing routine look like?
              </p>
            </div>
          </div>
        </section>

        <section className="grid md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
            <h2 className="text-xl font-bold text-emerald-700 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Daily
            </h2>
            <ul className="space-y-2">
              {dailyHabits.map((habit, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-secondary-700">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  {habit}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
            <h2 className="text-xl font-bold text-emerald-700 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Weekly
            </h2>
            <ul className="space-y-2">
              {weeklyHabits.map((habit, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-secondary-700">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  {habit}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
            <h2 className="text-xl font-bold text-emerald-700 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Monthly
            </h2>
            <ul className="space-y-2">
              {monthlyHabits.map((habit, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-secondary-700">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  {habit}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border-2 border-emerald-200">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Heart className="w-6 h-6 text-emerald-500" />
            Your Custom Plan
          </h2>
          <p className="text-secondary-600 mb-4">
            Circle or write the habits from this program you want to continue:
          </p>
          <div className="bg-white rounded-xl p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="font-medium text-emerald-700 mb-2">Non-negotiables (must do):</p>
                {[1, 2, 3].map(n => (
                  <div key={n} className="h-8 border-b border-emerald-200 mb-2"></div>
                ))}
              </div>
              <div>
                <p className="font-medium text-emerald-700 mb-2">Nice-to-haves:</p>
                {[1, 2, 3].map(n => (
                  <div key={n} className="h-8 border-b border-emerald-200 mb-2"></div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default MaintenancePlanGuide;
